import {
  classroomLifecycleLabel,
  formatDbDateTimeRangeVi,
  formatDbDateVi,
} from '../../../utils/dbDateDisplay'

export interface StudentCourseDetailData {
  studentInfo: {
    id: number
    registeredAt: string
    expireDate: string | null
    status?: string
  }
  courseInfo: {
    id: number
    code?: string
    name: string
    thumbnail: string
    description: string
    content: string
    method: string
    requiredInput: string
    suitableObject: string
    rule: string
    lecturerId?: number | null
    lecturer?: string | null
  }
  classroomInfo: {
    id: number
    name: string
    startDate: string
    endDate: string
    schedule: string
    address: string
    googleMeetLink: string | null
    zaloLink?: string | null
  } | null
  registrationInfo?: {
    orgFee: number | null
    fee: number | null
    registeredAt: string
    approvedAt: string | null
    status: string
  } | null
}

export interface ClassroomSessionItem {
  id: number
  title: string
  startTime: string
  endTime: string
  meetLink?: string | null
  recordingUrl?: string | null
  status: string
  displayStatus?: string
  sessionTitle?: string | null
  sessionOrderIndex?: number | null
}

export const MENTOR_PHONE = '0983213076'
export const MENTOR_ZALO_URL = `https://zalo.me/${MENTOR_PHONE}`

export function formatDateVi(dateString: string | Date | null | undefined): string {
  return formatDbDateVi(dateString)
}

export function formatDateTimeRange(
  start: string | Date,
  end: string | Date
): string {
  return formatDbDateTimeRangeVi(start, end)
}

export function sessionDisplayName(session: ClassroomSessionItem): string {
  return session.sessionTitle || session.title || `Buổi học #${session.id}`
}

export function isSessionCompleted(session: ClassroomSessionItem): boolean {
  return session.displayStatus === 'COMPLETED' || session.status === 'COMPLETED'
}

export function isLiveMethod(method: string): boolean {
  return method !== 'VIDEO'
}

export function pickNextSession(sessions: ClassroomSessionItem[]): ClassroomSessionItem | null {
  if (!sessions.length) return null
  const inProgress = sessions.find((s) => s.displayStatus === 'IN_PROGRESS')
  if (inProgress) return inProgress
  const soon = sessions.find((s) => s.displayStatus === 'UPCOMING_SOON')
  if (soon) return soon
  const scheduled = sessions
    .filter((s) => s.displayStatus === 'SCHEDULED' || (!isSessionCompleted(s) && s.displayStatus !== 'CANCELLED'))
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
  return scheduled[0] || null
}

export function resolveMeetLink(
  session: ClassroomSessionItem | null | undefined,
  classroomMeetLink: string | null | undefined
): string | null {
  return session?.meetLink || classroomMeetLink || null
}

export function registerStatusLabel(status: string | null | undefined): string {
  if (!status) return 'Chưa có'
  const map: Record<string, string> = {
    WAIT_PAYMENT: 'Chờ thanh toán',
    SUCCESS: 'Đã thanh toán',
    APPROVED: 'Đã xác nhận',
    LEARNING: 'Đang học',
    COMPLETED: 'Hoàn thành',
    FINISH: 'Hoàn thành',
    CANCELLED: 'Đã hủy',
    CANCEL: 'Đã hủy',
  }
  return map[status.toUpperCase()] || status
}

/** Badge khóa học: ưu tiên lifecycle lớp theo start/end date; giữ status thanh toán/hủy. */
export function courseDetailStatusLabel(
  registrationStatus: string | null | undefined,
  classroom: { startDate?: string | null; endDate?: string | null } | null | undefined
): string {
  const reg = String(registrationStatus || '')
    .toUpperCase()
    .trim()
  if (reg === 'WAIT_PAYMENT' || reg === 'CANCEL' || reg === 'CANCELLED') {
    return registerStatusLabel(reg)
  }
  if (classroom && (classroom.startDate || classroom.endDate)) {
    return classroomLifecycleLabel(classroom.startDate, classroom.endDate)
  }
  return registerStatusLabel(registrationStatus)
}
